/**
 * Seller-written listing titles sometimes reach us already HTML-escaped
 * ("B&amp;B Italië Camaleonda"): the text was escaped once upstream, in the
 * stored title or in the search index, and React escapes it again on render,
 * so the visitor sees the literal `&amp;` (WHO-5687).
 *
 * This decodes the common named entities and every numeric one back to plain
 * text, so the single escape React applies is the only one. It only ever
 * produces plain text; nothing here is meant for `dangerouslySetInnerHTML`.
 *
 * Text can arrive escaped more than once ("&amp;amp;"), so decoding repeats
 * until the string stops changing, with a small cap so a pathological input
 * cannot loop.
 */

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  euro: "€",
  eacute: "é",
  egrave: "è",
  euml: "ë",
  iuml: "ï",
  ouml: "ö",
  uuml: "ü",
  auml: "ä",
  ccedil: "ç",
  ntilde: "ñ",
  copy: "©",
  reg: "®",
  trade: "™",
  hellip: "…",
  laquo: "«",
  raquo: "»",
  lsquo: "‘",
  rsquo: "’",
  ldquo: "“",
  rdquo: "”",
};

const ENTITY_PATTERN = /&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);/gi;

const MAX_PASSES = 3;

const decodeOnce = (text: string) =>
  text.replace(ENTITY_PATTERN, (match, entity: string) => {
    if (entity[0] === "#") {
      const isHex = entity[1] === "x" || entity[1] === "X";
      const codePoint = parseInt(entity.slice(isHex ? 2 : 1), isHex ? 16 : 10);

      if (!Number.isFinite(codePoint) || codePoint <= 0 || codePoint > 0x10ffff) {
        return match;
      }

      try {
        return String.fromCodePoint(codePoint);
      } catch {
        return match;
      }
    }

    return NAMED_ENTITIES[entity.toLowerCase()] ?? match;
  });

/**
 * Decode HTML character entities in a piece of plain text. Text without any
 * entity comes back unchanged (same reference), so this is cheap to call on
 * every title.
 */
export const decodeHtmlEntities = (text: string): string => {
  if (!text.includes("&")) {
    return text;
  }

  let current = text;

  for (let pass = 0; pass < MAX_PASSES; pass += 1) {
    const next = decodeOnce(current);

    if (next === current) {
      break;
    }

    current = next;
  }

  return current;
};

/** Nullable variant for fields the API types as `string | null | undefined`. */
export const decodeHtmlEntitiesOrNull = <T extends string | null | undefined>(
  text: T
): T => (typeof text === "string" ? (decodeHtmlEntities(text) as T) : text);
